import type { AccountExport, Dashboard } from "../application/ports";
import type {
  DependentInvitationType,
  DependentProfileView,
  EventView,
  GiftListView,
  OnboardingInput,
  PassportVisibility,
  PassportPreferencesInput,
  NotificationView,
  NotificationPreferenceView,
  SecretSantaExclusionReason,
  TrustedPassportAccessView,
  WishInput
} from "../domain/cadohm";

export class ApiClientError extends Error {
  public constructor(public readonly code: string, public readonly status: number) {
    super(code);
    this.name = "ApiClientError";
  }
}

export class CadohmApiClient {
  public async register(input: { readonly displayName: string; readonly email: string; readonly password: string }): Promise<{
    readonly accountId: string;
    readonly expiresAt: string;
    readonly testOnlyVerificationCode?: string;
  }> {
    return this.request("/api/v1/auth/register", { body: JSON.stringify(input), method: "POST" });
  }

  public async verify(input: { readonly email: string; readonly code: string }): Promise<{ readonly myDohmStatus: string }> {
    return this.request("/api/v1/auth/verify", { body: JSON.stringify(input), method: "POST" });
  }

  public resendVerification(input: { readonly email: string }): Promise<{
    readonly accepted: true;
    readonly testOnlyVerificationCode?: string;
  }> {
    return this.request("/api/v1/auth/verification/resend", { body: JSON.stringify(input), method: "POST" });
  }

  public async login(input: { readonly email: string; readonly password: string }): Promise<void> {
    await this.request("/api/v1/auth/login", { body: JSON.stringify(input), method: "POST" });
  }

  public async logout(): Promise<void> {
    await this.request("/api/v1/auth/logout", {
      body: "{}",
      headers: { "x-cadohm-csrf": cookieValue("cadohm_csrf") },
      method: "POST"
    });
  }

  public requestRecovery(input: { readonly email: string }): Promise<{
    readonly accepted: true;
    readonly testOnlyRecoveryCode?: string;
  }> {
    return this.request("/api/v1/auth/recovery/request", { body: JSON.stringify(input), method: "POST" });
  }

  public async completeRecovery(input: {
    readonly email: string;
    readonly code: string;
    readonly newPassword: string;
  }): Promise<void> {
    await this.request("/api/v1/auth/recovery/complete", { body: JSON.stringify(input), method: "POST" });
  }

  public async completeOnboarding(input: OnboardingInput): Promise<Dashboard> {
    return this.request("/api/v1/onboarding", {
      body: JSON.stringify(input),
      headers: { "x-cadohm-csrf": cookieValue("cadohm_csrf") },
      method: "POST"
    });
  }

  public dashboard(): Promise<Dashboard> {
    return this.request("/api/v1/dashboard", { method: "GET" });
  }

  public requestMyDohmVerification(): Promise<Dashboard> {
    return this.request("/api/v1/my-dohm/verification/request", {
      body: "{}",
      headers: { "x-cadohm-csrf": cookieValue("cadohm_csrf") },
      method: "POST"
    });
  }

  public completeMyDohmVerification(code: string): Promise<Dashboard> {
    return this.request("/api/v1/my-dohm/verification/consume", {
      body: JSON.stringify({ code }),
      headers: { "x-cadohm-csrf": cookieValue("cadohm_csrf") },
      method: "POST"
    });
  }

  public addWish(input: WishInput): Promise<Dashboard> {
    return this.request("/api/v1/wishes", {
      body: JSON.stringify(input),
      headers: { "x-cadohm-csrf": cookieValue("cadohm_csrf") },
      method: "POST"
    });
  }

  public updatePassportVisibility(visibility: PassportVisibility): Promise<Dashboard> {
    return this.request("/api/v1/passport/visibility", {
      body: JSON.stringify({ visibility }),
      headers: { "x-cadohm-csrf": cookieValue("cadohm_csrf") },
      method: "PUT"
    });
  }

  public updatePassportPreferences(input: Required<PassportPreferencesInput>): Promise<Dashboard> {
    return this.request("/api/v1/passport/preferences", {
      body: JSON.stringify(input),
      headers: { "x-cadohm-csrf": cookieValue("cadohm_csrf") },
      method: "PUT"
    });
  }

  public inviteTrustedPassportViewer(email: string): Promise<{
    readonly invitationId: string;
    readonly testOnlyInvitationToken?: string;
  }> {
    return this.request("/api/v1/passport/trusted-invitations", {
      body: JSON.stringify({ email }),
      headers: { "x-cadohm-csrf": cookieValue("cadohm_csrf") },
      method: "POST"
    });
  }

  public async acceptTrustedPassportInvitation(token: string): Promise<void> {
    await this.request("/api/v1/passport-trusted-invitations/accept", {
      body: JSON.stringify({ token }),
      headers: { "x-cadohm-csrf": cookieValue("cadohm_csrf") },
      method: "POST"
    });
  }

  public trustedPassportAccess(): Promise<TrustedPassportAccessView> {
    return this.request("/api/v1/passport/trusted-access", { method: "GET" });
  }

  public notifications(): Promise<readonly NotificationView[]> {
    return this.request("/api/v1/notifications", { method: "GET" });
  }

  public async markNotificationRead(notificationId: string): Promise<void> {
    await this.request(`/api/v1/notifications/${encodeURIComponent(notificationId)}/read`, {
      body: "{}",
      headers: { "x-cadohm-csrf": cookieValue("cadohm_csrf") },
      method: "PUT"
    });
  }

  public notificationPreferences(): Promise<readonly NotificationPreferenceView[]> {
    return this.request("/api/v1/notification-preferences", { method: "GET" });
  }

  public async updateNotificationPreference(
    type: NotificationView["type"],
    inAppEnabled: boolean
  ): Promise<void> {
    await this.request(`/api/v1/notification-preferences/${encodeURIComponent(type)}`, {
      body: JSON.stringify({ inAppEnabled }),
      headers: { "x-cadohm-csrf": cookieValue("cadohm_csrf") },
      method: "PUT"
    });
  }

  public async revokeTrustedPassportViewer(viewerAccountId: string): Promise<void> {
    await this.request(`/api/v1/passport/trusted-viewers/${encodeURIComponent(viewerAccountId)}`, {
      body: "{}",
      headers: { "x-cadohm-csrf": cookieValue("cadohm_csrf") },
      method: "DELETE"
    });
  }

  public async addWishMerchantLink(wishId: string, input: {
    readonly isAffiliate: boolean;
    readonly merchantName: string;
    readonly url: string;
  }): Promise<void> {
    await this.request(`/api/v1/wishes/${encodeURIComponent(wishId)}/merchant-links`, {
      body: JSON.stringify(input),
      headers: { "x-cadohm-csrf": cookieValue("cadohm_csrf") },
      method: "POST"
    });
  }

  public createGiftList(title: string): Promise<GiftListView> {
    return this.request("/api/v1/lists", {
      body: JSON.stringify({ title }),
      headers: { "x-cadohm-csrf": cookieValue("cadohm_csrf") },
      method: "POST"
    });
  }

  public giftLists(): Promise<readonly GiftListView[]> {
    return this.request("/api/v1/lists", { method: "GET" });
  }

  public inviteGiftListEditor(listId: string, email: string): Promise<{
    readonly invitationId: string;
    readonly testOnlyInvitationToken?: string;
  }> {
    return this.request(`/api/v1/lists/${encodeURIComponent(listId)}/invitations`, {
      body: JSON.stringify({ email }),
      headers: { "x-cadohm-csrf": cookieValue("cadohm_csrf") },
      method: "POST"
    });
  }

  public acceptGiftListInvitation(token: string): Promise<{ readonly listId: string }> {
    return this.request("/api/v1/list-invitations/accept", {
      body: JSON.stringify({ token }),
      headers: { "x-cadohm-csrf": cookieValue("cadohm_csrf") },
      method: "POST"
    });
  }

  public async addGiftListItem(listId: string, title: string): Promise<void> {
    await this.request(`/api/v1/lists/${encodeURIComponent(listId)}/items`, {
      body: JSON.stringify({ title }),
      headers: { "x-cadohm-csrf": cookieValue("cadohm_csrf") },
      method: "POST"
    });
  }

  public async revokeGiftListEditor(listId: string, editorAccountId: string): Promise<void> {
    await this.request(`/api/v1/lists/${encodeURIComponent(listId)}/members/${encodeURIComponent(editorAccountId)}`, {
      body: "{}",
      headers: { "x-cadohm-csrf": cookieValue("cadohm_csrf") },
      method: "DELETE"
    });
  }

  public createBirthdayEvent(input: {
    readonly beneficiaryName: string;
    readonly decisionDeadline: string;
    readonly eventDate: string;
    readonly title: string;
  }): Promise<EventView> {
    return this.request("/api/v1/events", {
      body: JSON.stringify(input),
      headers: { "x-cadohm-csrf": cookieValue("cadohm_csrf") },
      method: "POST"
    });
  }

  public createSecretSantaEvent(input: {
    readonly avoidDirectReciprocity: boolean;
    readonly decisionDeadline: string;
    readonly eventDate: string;
    readonly title: string;
  }): Promise<EventView> {
    return this.request("/api/v1/secret-santa-events", {
      body: JSON.stringify(input),
      headers: { "x-cadohm-csrf": cookieValue("cadohm_csrf") },
      method: "POST"
    });
  }

  public createCollectiveGiftEvent(input: {
    readonly beneficiaryName: string;
    readonly decisionDeadline: string;
    readonly eventDate: string;
    readonly title: string;
  }): Promise<EventView> {
    return this.request("/api/v1/collective-gift-events", {
      body: JSON.stringify(input),
      headers: { "x-cadohm-csrf": cookieValue("cadohm_csrf") },
      method: "POST"
    });
  }

  public events(): Promise<readonly EventView[]> {
    return this.request("/api/v1/events", { method: "GET" });
  }

  public async openEvent(eventId: string): Promise<void> {
    await this.request(`/api/v1/events/${encodeURIComponent(eventId)}/open`, {
      headers: { "x-cadohm-csrf": cookieValue("cadohm_csrf") },
      method: "POST"
    });
  }

  public async closeEvent(eventId: string): Promise<void> {
    await this.request(`/api/v1/events/${encodeURIComponent(eventId)}/close`, {
      headers: { "x-cadohm-csrf": cookieValue("cadohm_csrf") },
      method: "POST"
    });
  }

  public inviteEventParticipant(eventId: string, email: string): Promise<{
    readonly invitationId: string;
    readonly testOnlyInvitationToken?: string;
  }> {
    return this.request(`/api/v1/events/${encodeURIComponent(eventId)}/invitations`, {
      body: JSON.stringify({ email }),
      headers: { "x-cadohm-csrf": cookieValue("cadohm_csrf") },
      method: "POST"
    });
  }

  public inviteEventBeneficiary(eventId: string, email: string): Promise<{
    readonly invitationId: string;
    readonly testOnlyInvitationToken?: string;
  }> {
    return this.request(`/api/v1/events/${encodeURIComponent(eventId)}/beneficiary-invitations`, {
      body: JSON.stringify({ email }),
      headers: { "x-cadohm-csrf": cookieValue("cadohm_csrf") },
      method: "POST"
    });
  }

  public acceptEventInvitation(token: string): Promise<{ readonly eventId: string }> {
    return this.request("/api/v1/event-invitations/accept", {
      body: JSON.stringify({ token }),
      headers: { "x-cadohm-csrf": cookieValue("cadohm_csrf") },
      method: "POST"
    });
  }

  public async revokeEventParticipant(eventId: string, participantAccountId: string): Promise<void> {
    await this.request(`/api/v1/events/${encodeURIComponent(eventId)}/members/${encodeURIComponent(participantAccountId)}`, {
      headers: { "x-cadohm-csrf": cookieValue("cadohm_csrf") },
      method: "DELETE"
    });
  }

  public async updateEventBeneficiaryOutcome(eventId: string, status: string): Promise<void> {
    await this.request(`/api/v1/events/${encodeURIComponent(eventId)}/beneficiary-outcome`, {
      body: JSON.stringify({ status }),
      headers: { "x-cadohm-csrf": cookieValue("cadohm_csrf") },
      method: "PUT"
    });
  }

  public async assignEventBuyer(eventId: string, buyerAccountId: string): Promise<void> {
    await this.request(
      `/api/v1/events/${encodeURIComponent(eventId)}/buyer/${encodeURIComponent(buyerAccountId)}`,
      {
        headers: { "x-cadohm-csrf": cookieValue("cadohm_csrf") },
        method: "PUT"
      }
    );
  }

  public async revokeEventBuyer(eventId: string, buyerAccountId: string): Promise<void> {
    await this.request(
      `/api/v1/events/${encodeURIComponent(eventId)}/buyer/${encodeURIComponent(buyerAccountId)}`,
      {
        headers: { "x-cadohm-csrf": cookieValue("cadohm_csrf") },
        method: "DELETE"
      }
    );
  }

  public async assignEventCoOrganizer(eventId: string, coOrganizerAccountId: string): Promise<void> {
    await this.request(
      `/api/v1/events/${encodeURIComponent(eventId)}/coorganizer/${encodeURIComponent(coOrganizerAccountId)}`,
      {
        headers: { "x-cadohm-csrf": cookieValue("cadohm_csrf") },
        method: "PUT"
      }
    );
  }

  public async revokeEventCoOrganizer(eventId: string, coOrganizerAccountId: string): Promise<void> {
    await this.request(
      `/api/v1/events/${encodeURIComponent(eventId)}/coorganizer/${encodeURIComponent(coOrganizerAccountId)}`,
      {
        headers: { "x-cadohm-csrf": cookieValue("cadohm_csrf") },
        method: "DELETE"
      }
    );
  }

  public async addEventGiftIdea(eventId: string, title: string): Promise<void> {
    await this.request(`/api/v1/events/${encodeURIComponent(eventId)}/gift-ideas`, {
      body: JSON.stringify({ title }),
      headers: { "x-cadohm-csrf": cookieValue("cadohm_csrf") },
      method: "POST"
    });
  }

  public async addEventGiftIdeaComment(eventId: string, ideaId: string, body: string): Promise<void> {
    await this.request(
      `/api/v1/events/${encodeURIComponent(eventId)}/gift-ideas/${encodeURIComponent(ideaId)}/comments`,
      {
        body: JSON.stringify({ body }),
        headers: { "x-cadohm-csrf": cookieValue("cadohm_csrf") },
        method: "POST"
      }
    );
  }

  public async setEventGiftIdeaVote(eventId: string, ideaId: string): Promise<void> {
    await this.request(
      `/api/v1/events/${encodeURIComponent(eventId)}/gift-ideas/${encodeURIComponent(ideaId)}/vote`,
      {
        body: "{}",
        headers: { "x-cadohm-csrf": cookieValue("cadohm_csrf") },
        method: "PUT"
      }
    );
  }

  public async removeEventGiftIdeaVote(eventId: string, ideaId: string): Promise<void> {
    await this.request(
      `/api/v1/events/${encodeURIComponent(eventId)}/gift-ideas/${encodeURIComponent(ideaId)}/vote`,
      {
        body: "{}",
        headers: { "x-cadohm-csrf": cookieValue("cadohm_csrf") },
        method: "DELETE"
      }
    );
  }

  public async decideEventGift(eventId: string, input: {
    readonly explanation: string;
    readonly ideaId: string;
  }): Promise<void> {
    await this.request(`/api/v1/events/${encodeURIComponent(eventId)}/gift-decisions`, {
      body: JSON.stringify(input),
      headers: { "x-cadohm-csrf": cookieValue("cadohm_csrf") },
      method: "POST"
    });
  }

  public async reserveEventGiftIdea(eventId: string, ideaId: string): Promise<void> {
    await this.request(
      `/api/v1/events/${encodeURIComponent(eventId)}/gift-ideas/${encodeURIComponent(ideaId)}/reservation`,
      {
        body: "{}",
        headers: { "x-cadohm-csrf": cookieValue("cadohm_csrf") },
        method: "POST"
      }
    );
  }

  public async cancelEventGiftReservation(eventId: string, ideaId: string): Promise<void> {
    await this.request(
      `/api/v1/events/${encodeURIComponent(eventId)}/gift-ideas/${encodeURIComponent(ideaId)}/reservation`,
      {
        body: "{}",
        headers: { "x-cadohm-csrf": cookieValue("cadohm_csrf") },
        method: "DELETE"
      }
    );
  }

  public async setEventExternalPot(eventId: string, input: {
    readonly provider: string;
    readonly url: string;
  }): Promise<void> {
    await this.request(`/api/v1/events/${encodeURIComponent(eventId)}/external-pot`, {
      body: JSON.stringify(input),
      headers: { "x-cadohm-csrf": cookieValue("cadohm_csrf") },
      method: "PUT"
    });
  }

  public async sharePassportWithEvent(eventId: string): Promise<void> {
    await this.request(`/api/v1/events/${encodeURIComponent(eventId)}/passport-share`, {
      body: "{}",
      headers: { "x-cadohm-csrf": cookieValue("cadohm_csrf") },
      method: "POST"
    });
  }

  public async revokePassportEventShare(eventId: string): Promise<void> {
    await this.request(`/api/v1/events/${encodeURIComponent(eventId)}/passport-share`, {
      body: "{}",
      headers: { "x-cadohm-csrf": cookieValue("cadohm_csrf") },
      method: "DELETE"
    });
  }

  public async addSecretSantaExclusion(eventId: string, input: {
    readonly accountAId: string;
    readonly accountBId: string;
    readonly reason: SecretSantaExclusionReason;
  }): Promise<void> {
    await this.request(`/api/v1/events/${encodeURIComponent(eventId)}/secret-santa/exclusions`, {
      body: JSON.stringify(input),
      headers: { "x-cadohm-csrf": cookieValue("cadohm_csrf") },
      method: "POST"
    });
  }

  public drawSecretSanta(eventId: string): Promise<{ readonly version: number }> {
    return this.request(`/api/v1/events/${encodeURIComponent(eventId)}/secret-santa/draw`, {
      body: "{}",
      headers: { "x-cadohm-csrf": cookieValue("cadohm_csrf") },
      method: "POST"
    });
  }

  public createDependentProfile(displayName: string): Promise<DependentProfileView> {
    return this.request("/api/v1/dependent-profiles", {
      body: JSON.stringify({ displayName }),
      headers: { "x-cadohm-csrf": cookieValue("cadohm_csrf") },
      method: "POST"
    });
  }

  public dependentProfiles(): Promise<readonly DependentProfileView[]> {
    return this.request("/api/v1/dependent-profiles", { method: "GET" });
  }

  public async addDependentWish(profileId: string, wish: WishInput): Promise<void> {
    await this.request(`/api/v1/dependent-profiles/${encodeURIComponent(profileId)}/wishes`, {
      body: JSON.stringify(wish),
      headers: { "x-cadohm-csrf": cookieValue("cadohm_csrf") },
      method: "POST"
    });
  }

  public inviteDependentProfile(
    profileId: string,
    email: string,
    invitationType: DependentInvitationType
  ): Promise<{ readonly invitationId: string; readonly testOnlyInvitationToken?: string }> {
    return this.request(`/api/v1/dependent-profiles/${encodeURIComponent(profileId)}/invitations`, {
      body: JSON.stringify({ email, invitationType }),
      headers: { "x-cadohm-csrf": cookieValue("cadohm_csrf") },
      method: "POST"
    });
  }

  public acceptDependentInvitation(token: string): Promise<{
    readonly invitationType: DependentInvitationType;
    readonly profileId: string;
  }> {
    return this.request("/api/v1/dependent-invitations/accept", {
      body: JSON.stringify({ token }),
      headers: { "x-cadohm-csrf": cookieValue("cadohm_csrf") },
      method: "POST"
    });
  }

  public async revokeDependentCoGuardian(profileId: string, coGuardianAccountId: string): Promise<void> {
    await this.request(
      `/api/v1/dependent-profiles/${encodeURIComponent(profileId)}/guardians/${encodeURIComponent(coGuardianAccountId)}`,
      {
        body: "{}",
        headers: { "x-cadohm-csrf": cookieValue("cadohm_csrf") },
        method: "DELETE"
      }
    );
  }

  public exportAccount(): Promise<AccountExport> {
    return this.request("/api/v1/account/export", { method: "GET" });
  }

  public async deleteAccount(input: { readonly confirmation: "SUPPRIMER"; readonly password: string }): Promise<void> {
    await this.request("/api/v1/account", {
      body: JSON.stringify(input),
      headers: { "x-cadohm-csrf": cookieValue("cadohm_csrf") },
      method: "DELETE"
    });
  }

  private async request<T>(path: string, init: RequestInit): Promise<T> {
    const headers = new Headers(init.headers);
    headers.set("content-type", "application/json");
    const response = await fetch(path, {
      ...init,
      credentials: "same-origin",
      headers
    });
    const payload: unknown = await response.json();
    if (!response.ok) {
      const code = errorCode(payload);
      throw new ApiClientError(code, response.status);
    }
    return payload as T;
  }
}

function cookieValue(name: string): string {
  for (const part of document.cookie.split(";")) {
    const [key, value] = part.trim().split("=", 2);
    if (key === name && value !== undefined) return value;
  }
  return "";
}

function errorCode(payload: unknown): string {
  if (typeof payload !== "object" || payload === null || !("error" in payload)) return "UNKNOWN_ERROR";
  const error = (payload as { readonly error?: unknown }).error;
  if (typeof error !== "object" || error === null || !("code" in error)) return "UNKNOWN_ERROR";
  const code = (error as { readonly code?: unknown }).code;
  return typeof code === "string" ? code : "UNKNOWN_ERROR";
}
